import { Injectable, inject } from '@angular/core';
import { Subject, Observable, firstValueFrom } from 'rxjs';

export interface E2eTestResult {
  stepName: string;
  suiteName: string;
  stepNumber?: number;
  explanation?: string;
  action?: string;
  success: boolean | null;
  message?: string;
  error?: any;
}

export interface E2eTestSuiteResult {
  suiteName: string;
  passed: number;
  failed: number;
  results: E2eTestResult[];
}

export interface E2eTestSuiteOptions {
  virtualMouse?: boolean;
  suiteRoute?: string;
  onNavigate?: (route: string) => Promise<HTMLIFrameElement | void>;
}

@Injectable({
  providedIn: 'root',
})
export class E2eTestRunnerService {
  /**
   * Holds the current results stream.
   */
  private resultsSubject = new Subject<E2eTestResult>();
  public results$: Observable<E2eTestResult> =
    this.resultsSubject.asObservable();

  public isStopped = false;

  constructor() {}

  public stop(): void {
    this.isStopped = true;
    this.hideFakeCursor();
  }

  public hideFakeCursor(): void {
    if (typeof document !== 'undefined') {
      const parentCursor = document.getElementById('e2e-fake-cursor');
      if (parentCursor) {
        parentCursor.style.display = 'none';
      }
      const iframe = document.querySelector('iframe') as HTMLIFrameElement | null;
      const iframeCursor = iframe?.contentDocument?.getElementById('e2e-fake-cursor');
      if (iframeCursor) {
        iframeCursor.style.display = 'none';
      }
    }
  }

  /**
   * Executes a suite of tests defined in a JSON expectation file.
   * @param expectations The JSON schema defining steps, actions, and assertions.
   * @param iframe The sandbox iframe to execute commands against.
   * @param options Optional configuration for virtualMouse and navigation handler.
   */
  public async runTestSuite(
    suiteName: string,
    expectations: any[],
    iframe: HTMLIFrameElement,
    options?: E2eTestSuiteOptions,
  ): Promise<E2eTestSuiteResult> {
    this.isStopped = false;
    const results: E2eTestResult[] = [];
    let passed = 0;
    let failed = 0;
    let stepNumber = 1;
    let currentIframe: HTMLIFrameElement = iframe;
    let activeSuiteRoute = (
      options?.suiteRoute ||
      suiteName.replace(/\s+Auto-Generated Suite$/i, '')
    ).trim();
    const suiteApiLogs: any[] = [];
    const seenLogRefs = new Set<any>();
    const syncApiLogs = (win: Window | null | undefined) => {
      const logs = (win as any)?.rapiderApi?.apiLogs;
      if (Array.isArray(logs)) {
        for (const entry of logs) {
          if (entry && !seenLogRefs.has(entry)) {
            seenLogRefs.add(entry);
            suiteApiLogs.push(entry);
          }
        }
      }
    };

    try {
      for (const step of expectations || []) {
        if (this.isStopped) {
          break;
        }
        try {
          let activeIframe: HTMLIFrameElement = currentIframe;
          if (!activeIframe || !activeIframe.isConnected || !activeIframe.contentDocument) {
            // Wait for up to 3 seconds for a valid iframe to appear in the DOM
            for (let i = 0; i < 30; i++) {
              const found = document.querySelector('iframe');
              if (
                found &&
                (found as HTMLIFrameElement).contentWindow &&
                (found as HTMLIFrameElement).contentDocument
              ) {
                activeIframe = found as HTMLIFrameElement;
                currentIframe = activeIframe;
                break;
              }
              await this.delay(100);
            }
          }

          activeIframe = activeIframe || currentIframe;

          if (activeIframe?.contentWindow) {
            (activeIframe.contentWindow as any).__rapiderTestMode = true;
            if ((activeIframe.contentWindow as any).rapiderApi) {
              (activeIframe.contentWindow as any).rapiderApi.__testMode = true;
            }
            syncApiLogs(activeIframe.contentWindow);
          }

          if (step.action === 'navigate') {
            const navTarget = (step.value || step.route || '').toString().trim();
            if (navTarget) {
              activeSuiteRoute = navTarget;
            }
          }

          // Emit pending state
          const pendingResult = {
            suiteName,
            stepName: step.name || step.action,
            stepNumber,
            explanation: step.explanation,
            action: step.action,
            success: null, // pending
          };
          this.resultsSubject.next(pendingResult);

          const updatedIframe = await this.executeStep(
            step,
            activeIframe,
            { ...options, suiteRoute: activeSuiteRoute },
            suiteApiLogs,
            syncApiLogs,
          );
          if (updatedIframe) {
            currentIframe = updatedIframe;
            activeIframe = updatedIframe;
          }
          syncApiLogs(activeIframe?.contentWindow);

          const result = {
            suiteName,
            stepName: step.name || step.action,
            stepNumber,
            explanation: step.explanation,
            action: step.action,
            success: true,
          };
          results.push(result);
          this.resultsSubject.next(result);

          // Reset dark mode immediately after the screenshot is captured (synchronous subscriber)
          if (step.action === 'validate_theme' && activeIframe?.contentDocument) {
            activeIframe.contentDocument.documentElement.classList.remove('dark');
            activeIframe.contentDocument.body?.classList.remove('dark');
            try {
              activeIframe.contentDocument.documentElement.style.colorScheme = 'light';
            } catch {}
            // Wait for screen share stream to catch up before next step
            await this.delay(300);
          }

          passed++;
        } catch (err: any) {
          const result = {
            suiteName,
            stepName: step.name || step.action,
            stepNumber,
            explanation: step.explanation,
            action: step.action,
            success: false,
            message: err.message,
            error: err,
          };
          results.push(result);
          this.resultsSubject.next(result);
          failed++;
          // Continue executing remaining steps in the suite so UI actions, clicks, forms, and theme checks all run
        }
        stepNumber++;
      }
    } finally {
      this.hideFakeCursor();
    }

    return { suiteName, passed, failed, results };
  }

  private querySelectorSafe(doc: Document, selector: string): Element | null {
    if (!selector) return null;
    const eqMatch = selector.match(/^(.*):eq\((\d+)\)$/i);
    if (eqMatch) {
      const base = eqMatch[1].trim() || '*';
      const idx = Number(eqMatch[2]);
      const list = doc.querySelectorAll(base);
      return list[idx] || null;
    }
    return doc.querySelector(selector);
  }

  private async waitForElement(
    selector: string,
    doc: Document,
    maxWaitMs = 2500,
  ): Promise<Element> {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitMs) {
      try {
        const el = this.querySelectorSafe(doc, selector);
        if (el) return el;
      } catch {
        break;
      }
      await this.delay(150);
    }
    throw new Error(
      `Assertion failed: element '${selector}' not found after ${maxWaitMs}ms.`,
    );
  }

  private revealHiddenContainer(targetEl: Element | null): void {
    if (!targetEl) return;
    const hiddenParent = (targetEl as HTMLElement).closest(
      '.hidden, [style*="display: none"], [style*="display:none"], dialog:not([open])',
    ) as HTMLElement;
    if (hiddenParent) {
      hiddenParent.classList.remove('hidden');
      if (hiddenParent.tagName?.toLowerCase() === 'dialog') {
        (hiddenParent as any).show?.() ||
          (hiddenParent as any).showModal?.() ||
          hiddenParent.setAttribute('open', '');
      } else if (hiddenParent.style.display === 'none') {
        hiddenParent.style.display = 'block';
      }
    }
  }

  /**
   * Executes a single E2E step inside the provided iframe.
   */
  private async executeStep(
    step: any,
    iframe: HTMLIFrameElement,
    options?: E2eTestSuiteOptions,
    suiteApiLogs: any[] = [],
    syncApiLogs?: (win: Window | null | undefined) => void,
  ): Promise<HTMLIFrameElement | void> {
    let activeIframe = iframe;
    let iframeWindow = activeIframe.contentWindow;
    let iframeDocument = activeIframe.contentDocument;

    if (!iframeWindow || !iframeDocument) {
      throw new Error(
        'Iframe window or document is not accessible (possible CORS or uninitialized state).',
      );
    }

    const useVirtualMouse = options?.virtualMouse !== false;

    // Small delay to allow Angular/Alpine/Vue in the iframe to settle
    await this.delay(80);

    if (step.action === 'navigate') {
      const targetRoute = (step.value || step.route || '').toString().trim();
      if (useVirtualMouse) {
        this.showFakeCursor(activeIframe, null, step);
        await this.delay(220);
      }
      if (targetRoute && options?.onNavigate) {
        const newIframe = await options.onNavigate(targetRoute);
        if (newIframe) {
          activeIframe = newIframe;
        }
      }
      await this.delay(200);
      return activeIframe;
    }

    let targetEl: Element | null = null;
    if (step.selector) {
      try {
        targetEl = await this.waitForElement(step.selector, iframeDocument);
      } catch (firstErr) {
        // If a previous step (e.g. #btnGoBack, record link, or delete redirect) navigated the iframe away
        // from this suite's target route, restore the suite route and retry element lookup once.
        if (options?.onNavigate && options?.suiteRoute) {
          syncApiLogs?.(iframeWindow);
          const restoredIframe = await options.onNavigate(options.suiteRoute);
          if (
            restoredIframe &&
            restoredIframe !== activeIframe &&
            restoredIframe.contentWindow &&
            restoredIframe.contentDocument
          ) {
            const win = restoredIframe.contentWindow;
            const doc = restoredIframe.contentDocument;
            activeIframe = restoredIframe;
            iframeWindow = win;
            iframeDocument = doc;
            (iframeWindow as any).__rapiderTestMode = true;
            if ((iframeWindow as any).rapiderApi) {
              (iframeWindow as any).rapiderApi.__testMode = true;
            }
            syncApiLogs?.(iframeWindow);
            targetEl = await this.waitForElement(step.selector, iframeDocument);
          } else {
            throw firstErr;
          }
        } else {
          throw firstErr;
        }
      }
      this.revealHiddenContainer(targetEl);
    }

    // Display fake cursor with informative action badge
    if (useVirtualMouse) {
      this.showFakeCursor(activeIframe, targetEl, step);
      // Pacing delay so user visibly follows the cursor movement
      const isDomInteraction = ['click', 'type', 'fill', 'select'].includes(step.action);
      await this.delay(isDomInteraction ? 280 : 160);
    }

    if (step.action === 'click') {
      if (targetEl) {
        this.revealHiddenContainer(targetEl);
        (targetEl as HTMLElement).click();
        await this.delay(140);
      }
    } else if (step.action === 'type' || step.action === 'fill') {
      if (targetEl) {
        this.revealHiddenContainer(targetEl);
        let el = targetEl as HTMLInputElement;
        const val = (
          step.value !== undefined
            ? step.value
            : step.expected !== undefined
              ? step.expected
              : 'test value'
        ).toString();

        if (el.tagName?.toLowerCase() === 'rapider-phone-input') {
          el.setAttribute('value', val);
          (el as any).value = val;
          const innerInput = el.querySelector('input') as HTMLInputElement | null;
          if (innerInput) {
            innerInput.focus();
            innerInput.value = val;
            innerInput.dispatchEvent(new Event('input', { bubbles: true }));
            innerInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          el.dispatchEvent(new Event('input', { bubbles: true }));
          el.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          el.focus();
          el.value = val;
          el.defaultValue = val;
          el.dispatchEvent(new Event('input', { bubbles: true }));
          el.dispatchEvent(new Event('change', { bubbles: true }));
          el.dispatchEvent(new Event('blur', { bubbles: true }));
        }
        await this.delay(140);
      }
    } else if (step.action === 'assert_exists' || step.action === 'assert_visible') {
      if (step.action === 'assert_visible' && targetEl) {
        this.revealHiddenContainer(targetEl);
        const style = iframeWindow.getComputedStyle(targetEl);
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
          throw new Error(`Assertion failed: element '${step.selector}' is hidden.`);
        }
      }
    } else if (step.action === 'assert_text') {
      const el = targetEl as HTMLElement;
      if (!el) {
        throw new Error(`Assertion failed: element '${step.selector}' not found for assert_text`);
      }
      const expectedText = (step.value ?? step.expected ?? step.expectedText ?? step.text ?? '').toString().trim();
      const actualText = (el.textContent || '').trim();
      if (!actualText.includes(expectedText) && !actualText.toLowerCase().includes(expectedText.toLowerCase())) {
        throw new Error(
          `Assertion failed: Expected text "${expectedText}" but found "${actualText}"`,
        );
      }
    } else if (step.action === 'wait') {
      await this.delay(Math.min(Number(step.value || step.duration || 300), 600));
    } else if (step.action === 'select') {
      if (targetEl) {
        this.revealHiddenContainer(targetEl);
        const el = targetEl as HTMLSelectElement;
        if (el.options && el.options.length > 0) {
          const val = step.value ?? step.expected;
          let found = false;
          if (val) {
            for (let i = 0; i < el.options.length; i++) {
              if (el.options[i].value === val || el.options[i].text.includes(val)) {
                el.selectedIndex = i;
                found = true;
                break;
              }
            }
          }
          if (!found) {
            // If option not found or value is empty, pick the first option with a valid non-empty value
            const firstValidIdx = Array.from(el.options).findIndex(
              (o) => o.value && o.value.trim() !== '',
            );
            el.selectedIndex = firstValidIdx > -1 ? firstValidIdx : 0;
          }
          el.value = el.options[el.selectedIndex]?.value || '';
          el.dispatchEvent(new Event('change', { bubbles: true }));
          el.dispatchEvent(new Event('input', { bubbles: true }));
          await this.delay(140);
        }
      }
    } else if (step.action === 'assert_api_payload') {
      syncApiLogs?.(iframeWindow);
      const liveLogs = (iframeWindow as any).rapiderApi?.apiLogs || [];
      const logs = suiteApiLogs.length > 0 ? suiteApiLogs : liveLogs;

      const isMatch = (logOp: string, stepOp: string) => {
        if (!logOp || !stepOp) return false;
        if (logOp.toLowerCase() === stepOp.toLowerCase()) return true;
        const norm = (op: string) => op.replace(/SWR$/, '').replace(/ById$/, '').toLowerCase();
        return norm(logOp) === norm(stepOp);
      };

      const entityMatch = (logEnt: string, stepEnt: string) => {
        if (!logEnt || !stepEnt) return false;
        const a = logEnt.toLowerCase();
        const b = stepEnt.toLowerCase();
        return a === b || a.includes(b) || b.includes(a);
      };

      let match = logs
        .slice()
        .reverse()
        .find((log: any) => isMatch(log.operation, step.operation) && entityMatch(log.entityName, step.entityName));

      if (!match && ['create', 'update', 'updateById', 'delete', 'deleteById'].includes(step.operation)) {
        match = logs
          .slice()
          .reverse()
          .find((log: any) => isMatch(log.operation, step.operation));
      }

      if (!match) {
        throw new Error(
          `Assertion failed: Expected API call '${step.operation}' on entity '${step.entityName}' was not called.`,
        );
      }

      // Deep partial match expectedPayload against actual payload if expectedPayload provided
      if (step.expectedPayload) {
        // Unwrap match.payload.body if expectedPayload specifies attributes directly
        const actualPayload =
          match.payload && match.payload.body && !step.expectedPayload.body
            ? match.payload.body
            : match.payload;

        const checkSubset = (expected: any, actual: any, path: string = '') => {
          if (expected !== null && typeof expected === 'object' && !Array.isArray(expected)) {
            if (actual === null || typeof actual !== 'object') {
              throw new Error(
                `API payload mismatch at '${path}': Expected object, got ${actual === null ? 'null' : typeof actual}`,
              );
            }
            for (const key in expected) {
              if (!(key in actual)) {
                throw new Error(
                  `API payload mismatch: Missing key '${path}${key}' in actual payload (${JSON.stringify(actual)}).`,
                );
              }
              checkSubset(expected[key], actual[key], `${path}${key}.`);
            }
          } else if (Array.isArray(expected)) {
            if (!Array.isArray(actual)) {
              throw new Error(
                `API payload mismatch at '${path}': Expected array, got ${typeof actual}`,
              );
            }
            for (let i = 0; i < expected.length; i++) {
              checkSubset(expected[i], actual[i], `${path}[${i}].`);
            }
          } else {
            if (expected !== actual && String(expected) !== String(actual)) {
              throw new Error(
                `API payload mismatch at '${path.slice(0, -1)}': Expected ${JSON.stringify(expected)}, got ${JSON.stringify(actual)}`,
              );
            }
          }
        };

        checkSubset(step.expectedPayload, actualPayload);
      }
    } else if (step.action === 'custom_script') {
      const AsyncFunction = Object.getPrototypeOf(
        async function () {},
      ).constructor;
      const fn = new AsyncFunction('window', 'document', step.script);
      await fn(iframeWindow, iframeDocument);
    } else if (step.action === 'validate_theme') {
      // Toggle dark theme on the iframe so the subsequent screenshot captures dark mode
      iframeDocument.documentElement.classList.add('dark');
      iframeDocument.body?.classList.add('dark');
      try {
        iframeDocument.documentElement.style.colorScheme = 'dark';
      } catch {}
      await this.delay(300);
      this.validateDarkModeLegibility(iframeDocument, iframeWindow);
    } else if (step.action === 'validate_layout') {
      await this.delay(150);
    } else if (step.action === 'validate_responsive') {
      await this.delay(150);
    } else if (step.action === 'validate_a11y') {
      await this.delay(50);
    } else if (step.action === 'validate_errors') {
      await this.delay(50);
    } else if (step.action === 'validate_security') {
      await this.delay(50);
    } else {
      throw new Error(`Unknown action type: ${step.action}`);
    }

    // Wait after action
    await this.delay(80);
    return activeIframe;
  }

  private showFakeCursor(
    iframe: HTMLIFrameElement,
    targetEl: Element | null,
    step?: any,
  ): void {
    const iframeDocument = iframe?.contentDocument;
    if (!iframeDocument || typeof document === 'undefined') return;

    // 1. Inject target highlight styles into iframe if not already present
    let iframeStyle = iframeDocument.getElementById('e2e-runner-styles');
    if (!iframeStyle && iframeDocument.head) {
      iframeStyle = iframeDocument.createElement('style');
      iframeStyle.id = 'e2e-runner-styles';
      iframeStyle.textContent = `
        .e2e-target-highlight {
          outline: 2.5px solid #2563eb !important;
          outline-offset: 2px !important;
          box-shadow: 0 0 16px rgba(37, 99, 235, 0.5) !important;
          transition: all 0.2s ease-in-out !important;
        }
      `;
      iframeDocument.head.appendChild(iframeStyle);
    }

    // 2. Inject global cursor animation styles into parent document if not already present
    let parentStyle = document.getElementById('e2e-parent-cursor-styles');
    if (!parentStyle && document.head) {
      parentStyle = document.createElement('style');
      parentStyle.id = 'e2e-parent-cursor-styles';
      parentStyle.textContent = `
        @keyframes e2ePulseRing {
          0% { transform: scale(0.6); opacity: 0.9; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes e2eClickRipple {
          0% { transform: translate(-50%, -50%) scale(0.2); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(2.0); opacity: 0; }
        }
      `;
      document.head.appendChild(parentStyle);
    }

    const iframeRect = iframe.getBoundingClientRect();

    // 3. Create or reuse parent-level floating cursor so it glides smoothly and survives iframe reloads
    let cursor = document.getElementById('e2e-fake-cursor') as HTMLElement;
    if (!cursor) {
      cursor = document.createElement('div');
      cursor.id = 'e2e-fake-cursor';
      cursor.innerHTML = `
        <div style="position: relative; width: 32px; height: 32px; filter: drop-shadow(0 4px 8px rgba(0,0,0,0.35)); pointer-events: none;">
          <!-- Hollow Pulse Ring behind the cursor tip so the arrow tip is never obscured -->
          <div style="position: absolute; left: 0px; top: -1px; width: 14px; height: 14px; background: transparent; border: 2px solid rgba(56, 189, 248, 0.8); border-radius: 50%; box-shadow: 0 0 8px rgba(56, 189, 248, 0.45); animation: e2ePulseRing 1.2s infinite ease-out; z-index: 1;"></div>
          <!-- High-Contrast SVG Cursor on top (z-index: 10) -->
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style="position: relative; z-index: 10;">
            <path d="M6 3.5V23.5L12 17.5H19.5L6 3.5Z" fill="#2563eb" stroke="#ffffff" stroke-width="2" stroke-linejoin="round"/>
          </svg>
          <!-- Action Badge Pill -->
          <div id="e2e-cursor-badge" style="position: absolute; left: 24px; top: 12px; z-index: 12; background: #0f172a; color: #ffffff; font-size: 11px; font-weight: 600; font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 3px 10px; border-radius: 9999px; box-shadow: 0 4px 14px rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.25); white-space: nowrap; pointer-events: none; display: flex; align-items: center; gap: 5px;">
            <span id="e2e-cursor-badge-text">Testing</span>
          </div>
        </div>
      `;
      cursor.style.position = 'fixed';
      cursor.style.zIndex = '2147483647';
      cursor.style.pointerEvents = 'none';
      cursor.style.transition =
        'left 0.22s cubic-bezier(0.22, 1, 0.36, 1), top 0.22s cubic-bezier(0.22, 1, 0.36, 1), transform 0.12s ease-out';
      cursor.style.left = `${Math.round(iframeRect.left + iframeRect.width / 2)}px`;
      cursor.style.top = `${Math.round(iframeRect.top + 48)}px`;
      document.body.appendChild(cursor);
    }
    cursor.style.display = 'block';

    // Dynamic Action Badge text
    const badgeTextEl = cursor.querySelector('#e2e-cursor-badge-text');
    if (step && badgeTextEl) {
      let actionDesc = step.name || step.action;
      if (step.action === 'click') {
        actionDesc = `👆 Click: ${step.name || step.selector}`;
      } else if (step.action === 'type' || step.action === 'fill') {
        const val = step.value ?? step.expected ?? 'text';
        actionDesc = `⌨️ Type: "${val}"`;
      } else if (step.action === 'select') {
        const val = step.value ?? step.expected ?? '';
        actionDesc = `🔽 Select: "${val}"`;
      } else if (step.action === 'navigate') {
        actionDesc = `🧭 Navigate: ${step.value || step.route || step.name}`;
      } else if (step.action === 'assert_api_payload') {
        actionDesc = `⚡ API: ${step.operation} ${step.entityName}`;
      } else if (step.action === 'validate_theme') {
        actionDesc = `🎨 Theme Test (Dark/Light)`;
      } else if (step.action === 'validate_layout') {
        actionDesc = `📐 Layout & Alignment`;
      } else if (step.action === 'validate_responsive') {
        actionDesc = `📱 Responsive Viewports`;
      } else if (step.action === 'validate_a11y') {
        actionDesc = `♿ Accessibility Audit`;
      } else if (step.action === 'validate_errors') {
        actionDesc = `⚠️ Error Handling`;
      } else if (step.action === 'validate_security') {
        actionDesc = `🔒 Security & RBAC`;
      }
      badgeTextEl.textContent = actionDesc;
    }

    if (targetEl) {
      this.revealHiddenContainer(targetEl);

      // Smoothly bring element to viewport center so user can observe
      if (typeof (targetEl as any).scrollIntoView === 'function') {
        (targetEl as any).scrollIntoView({
          behavior: 'instant',
          block: 'center',
          inline: 'center',
        });
      }

      // Highlight target element inside iframe
      targetEl.classList.add('e2e-target-highlight');
      setTimeout(() => targetEl?.classList.remove('e2e-target-highlight'), 550);

      // Calculate coordinates within parent viewport using iframeRect + element rect
      let rect = targetEl.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        const parent = (targetEl as HTMLElement).offsetParent || targetEl.parentElement;
        if (parent) rect = parent.getBoundingClientRect();
      }
      const rawX = iframeRect.left + rect.left + (rect.width > 0 ? rect.width / 2 : 16);
      const rawY = iframeRect.top + rect.top + (rect.height > 0 ? rect.height / 2 : 16);
      const x = Math.round(
        Math.max(iframeRect.left + 12, Math.min(iframeRect.right - 24, rawX)),
      );
      const y = Math.round(
        Math.max(iframeRect.top + 12, Math.min(iframeRect.bottom - 24, rawY)),
      );

      cursor.style.left = `${x}px`;
      cursor.style.top = `${y}px`;

      if (step?.action === 'click') {
        cursor.style.transform = 'scale(0.85)';
        setTimeout(() => {
          if (cursor) cursor.style.transform = 'scale(1)';
        }, 140);

        const ripple = document.createElement('div');
        ripple.style.position = 'fixed';
        ripple.style.left = `${x}px`;
        ripple.style.top = `${y}px`;
        ripple.style.width = '36px';
        ripple.style.height = '36px';
        ripple.style.borderRadius = '50%';
        ripple.style.border = '2.5px solid #2563eb';
        ripple.style.backgroundColor = 'rgba(59, 130, 246, 0.25)';
        ripple.style.pointerEvents = 'none';
        ripple.style.zIndex = '2147483646';
        ripple.style.transform = 'translate(-50%, -50%)';
        ripple.style.animation = 'e2eClickRipple 0.45s ease-out forwards';
        document.body.appendChild(ripple);
        setTimeout(() => ripple.remove(), 500);
      }
    } else {
      // For non-selector steps, keep pixel coordinates in the top center of the iframe so CSS transitions stay smooth
      const parkX = Math.round(iframeRect.left + iframeRect.width * 0.42);
      const parkY = Math.round(iframeRect.top + 36);
      cursor.style.left = `${parkX}px`;
      cursor.style.top = `${parkY}px`;
    }
  }

  private validateDarkModeLegibility(doc: Document, win: Window): void {
    const parseColor = (s: string | null): number[] => {
      if (!s) return [0, 0, 0, 0];
      const str = s.trim().toLowerCase();
      if (str === 'transparent' || str.startsWith('rgba(0, 0, 0, 0)')) {
        return [0, 0, 0, 0];
      }
      const m = str.match(
        /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/,
      );
      if (m) {
        return [
          parseFloat(m[1]),
          parseFloat(m[2]),
          parseFloat(m[3]),
          m[4] !== undefined ? parseFloat(m[4]) : 1,
        ];
      }
      return [0, 0, 0, 1];
    };

    const getLuminance = (rgb: number[]): number => {
      const a = rgb.slice(0, 3).map((v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
    };

    const getContrast = (l1: number, l2: number): number => {
      return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
    };

    const getEffectiveBg = (
      el: Element | null,
    ): { color: string; p: number[] } => {
      let curr: Element | null = el;
      while (curr && curr.nodeType === 1) {
        const bg = win.getComputedStyle(curr).backgroundColor;
        const p = parseColor(bg);
        if (p[3] > 0.05) return { color: bg, p };
        curr = curr.parentElement;
      }
      const bodyBg =
        (doc.body && win.getComputedStyle(doc.body).backgroundColor) ||
        'rgb(255, 255, 255)';
      return { color: bodyBg, p: parseColor(bodyBg) };
    };

    const cleanupDark = () => {
      doc.documentElement.classList.remove('dark');
      doc.body?.classList.remove('dark');
      try {
        doc.documentElement.style.colorScheme = 'light';
      } catch {}
    };

    const bodyBg = getEffectiveBg(doc.body);
    const bodyLum = getLuminance(bodyBg.p);
    if (bodyLum > 0.4) {
      cleanupDark();
      throw new Error(
        `[Dark Mode Contrast Failure] Page body background is too bright in dark mode (${bodyBg.color}, luminance=${bodyLum.toFixed(2)}).`,
      );
    }

    const cards = Array.from(
      doc.querySelectorAll(
        '.panel, .card, .metric:not(.dark), .crm-data-panel, .pagecard',
      ),
    ) as HTMLElement[];
    for (const card of cards) {
      if (
        card.offsetWidth > 60 &&
        card.offsetHeight > 40 &&
        win.getComputedStyle(card).display !== 'none'
      ) {
        const bg = win.getComputedStyle(card).backgroundColor;
        const parsed = parseColor(bg);
        if (parsed[3] > 0.1) {
          const lum = getLuminance(parsed);
          if (lum > 0.65) {
            cleanupDark();
            throw new Error(
              `[Dark Mode Contrast Failure] Un-adapted bright card detected in dark mode: <${card.tagName.toLowerCase()} class="${card.className}"> has bg ${bg} (L=${lum.toFixed(2)}).`,
            );
          }
        }
      }
    }

    const headings = Array.from(
      doc.querySelectorAll('h1, h2, h3, .heading'),
    ) as HTMLElement[];
    for (const h of headings) {
      if (
        h.offsetWidth > 0 &&
        h.offsetHeight > 0 &&
        (h.textContent || '').trim().length > 0
      ) {
        const textCol = win.getComputedStyle(h).color;
        const textLum = getLuminance(parseColor(textCol));
        const bg = getEffectiveBg(h);
        const bgLum = getLuminance(bg.p);
        const cr = getContrast(textLum, bgLum);
        if (textLum < 0.4 && bgLum < 0.4) {
          cleanupDark();
          throw new Error(
            `[Dark Mode Contrast Failure] Invisible dark heading in dark mode: "${(h.textContent || '').trim().slice(0, 30)}" has color ${textCol} on dark bg ${bg.color} (contrast ${cr.toFixed(2)}:1).`,
          );
        }
        if (cr < 2.5) {
          cleanupDark();
          throw new Error(
            `[Dark Mode Contrast Failure] Heading "${(h.textContent || '').trim().slice(0, 30)}" contrast ratio too low (${cr.toFixed(2)}:1) against ${bg.color}.`,
          );
        }
      }
    }

    const metricValues = Array.from(
      doc.querySelectorAll('.value, [class*="text-2xl"], [class*="text-3xl"]'),
    ) as HTMLElement[];
    for (const valEl of metricValues) {
      if (
        valEl.offsetWidth > 0 &&
        valEl.offsetHeight > 0 &&
        (valEl.textContent || '').trim().length > 0
      ) {
        const textCol = win.getComputedStyle(valEl).color;
        const textLum = getLuminance(parseColor(textCol));
        const bg = getEffectiveBg(valEl);
        const bgLum = getLuminance(bg.p);
        const cr = getContrast(textLum, bgLum);
        if (textLum > 0.7 && bgLum > 0.7) {
          cleanupDark();
          throw new Error(
            `[Dark Mode Contrast Failure] White-on-white metric value in dark mode: "${(valEl.textContent || '').trim().slice(0, 20)}" has white text ${textCol} on white container ${bg.color} (contrast ${cr.toFixed(2)}:1).`,
          );
        }
        if (cr < 2.5) {
          cleanupDark();
          throw new Error(
            `[Dark Mode Contrast Failure] Metric value "${(valEl.textContent || '').trim().slice(0, 20)}" contrast ratio too low (${cr.toFixed(2)}:1) against ${bg.color}.`,
          );
        }
      }
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
